import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../../shared/store/authStore'
import TabletHeader from '../../../shared/components/TabletHeader'
import TabletScreen from '../../../shared/components/TabletScreen'
import { Badge, LoadingSpinner } from '../../../shared/components/UI'
import { dashboardApi, announcementsApi } from '../../../shared/lib/api'
import { Download, Upload, Flame, Wind, Truck, BarChart3 } from 'lucide-react'

// Mapping WorkOrder statuses to tablet routes
const TASK_ROUTE = {
  'pending':        '/app/spindle',
  'in_progress':    '/app/spindle', // Re-scan or check position
  'completed':      '/app/output',  // If it was just completed but not yet handed off (hypothetical)
}

const STATUS_BADGE = {
  pending:     { v: 'amber', l: 'Ready for Loading' },
  in_progress: { v: 'teal',  l: 'In Spindle' },
}

export default function DashboardPage() {
  const { user }   = useAuthStore()
  const navigate   = useNavigate()
  const [loading, setLoading] = useState(true)
  const [data, setData]       = useState({ announcements: [] })

  useEffect(() => {
    announcementsApi.listForUser().then((annRes) => {
      setData({
        announcements: annRes.data.data || []
      })
    }).catch(err => {
      console.error('Failed to load dashboard data', err)
    }).finally(() => setLoading(false))
  }, [])

  if (loading) return <TabletScreen><div className="flex items-center justify-center h-full"><LoadingSpinner size="lg" teal /></div></TabletScreen>

  return (
    <TabletScreen>
      <TabletHeader
        title={`Hello, ${user?.name?.split(' ')[0] || 'Supervisor'}`}
        role="Loading Supervisor"
        unit={user?.unitName || 'Unit'}
        teal
      />
      <div className="p-4">

        {/* Announcements */}
        {data.announcements.map(a => (
          <div key={a.id} className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">📢</span>
              <p className="text-sm font-bold text-amber-900">{a.title}</p>
            </div>
            <p className="text-xs text-amber-800 leading-relaxed">{a.message}</p>
          </div>
        ))}

        {/* Workflow Stages Matrix */}
        <p className="section-heading mb-4 px-1">Operations</p>
        <div className="grid grid-cols-2 gap-4 mb-8">
          {[
            { label: 'Spindle Load',  route: '/app/spindle', icon: <Download size={22} />, color: 'bg-blue-600', shadow: 'shadow-blue-100', desc: 'Mount rolls' },
            { label: 'Record Output', route: '/app/output',  icon: <Upload size={22} />, color: 'bg-emerald-600', shadow: 'shadow-emerald-100', desc: 'Final weight' },
            { label: 'Boiling',       route: '/app/boiling', icon: <Flame size={22} />, color: 'bg-orange-500', shadow: 'shadow-orange-100', desc: 'Heat treat' },
            { label: 'Formation',     route: '/app/cone',    icon: <Wind size={22} />, color: 'bg-indigo-600', shadow: 'shadow-indigo-100', desc: 'Winding' },
            { label: 'Dispatch',      route: '/app/dispatch',icon: <Truck size={22} />, color: 'bg-rose-500', shadow: 'shadow-rose-100', desc: 'Shipping' },
            { label: 'Reports',       route: '/app/dashboard',icon: <BarChart3 size={22} />, color: 'bg-slate-600', shadow: 'shadow-slate-100', desc: 'View stats' },
          ].map(({ label, route, icon, color, shadow, desc }) => (
            <button 
              key={label} 
              onClick={() => navigate(route)}
              className="group relative flex flex-col items-start p-5 rounded-[2rem] bg-white border border-gray-100 shadow-sm transition-all active:scale-95 hover:shadow-md overflow-hidden"
            >
              <div className={`w-12 h-12 ${color} rounded-2xl flex items-center justify-center mb-4 shadow-lg ${shadow} group-hover:scale-110 transition-transform`}>
                <div className="filter drop-shadow-sm text-white">{icon}</div>
              </div>
              <span className="text-sm font-extrabold text-gray-800">{label}</span>
              <span className="text-[10px] text-gray-400 font-semibold mt-0.5">{desc}</span>
              
              {/* Subtle accent corner */}
              <div className={`absolute top-0 right-0 w-16 h-16 -mr-8 -mt-8 ${color} opacity-[0.03] rounded-full group-hover:opacity-[0.08] transition-opacity`} />
            </button>
          ))}
        </div>
      </div>
    </TabletScreen>
  )
}
